import React, { useCallback, useMemo } from 'react';
import FormFieldset from '@components/form/FormFieldset';
import CardPlan from '@components/card/CardPlan';
import { Product, ProductAndCoveragesResponse, ProductPricingResponse } from '@apis/models';
import { isProdEnv, sanitizeId } from '@helpers/utils';
import { getCurrentThemeConfig } from '@app/core/theme-config';
import { BDSMainColor } from '@constants/bds.constants';
import { Address } from '@app/models';
import { getBrand } from '@helpers/brand.utils';
import { Brand } from '@constants/brands';
import {
  ProductComparisonCardsMicroFrontend,
  Theme,
  Tenant,
} from '@ftdr/product-comparison-cards-micro-frontend';
import useGlobalAlert from '@app/core/GlobalAlertModal';

export interface FormNewOrderPlanSelectionProps {
  /** the selected PVID to select the component */
  selectedProductId: string;
  /** the selected product plan ID (used when PVID doesn't match due to service fee changes) */
  selectedProductPlanId?: string;
  /** used for styling to determine how to present the component */
  isSmall: boolean;
  /** is set to true, plans are hidden and show skeletons instead */
  isLoading: boolean;
  /** if set to a value, the # of skeleton plans to display will be this value.  If not set, default to 3. */
  loadingSkeletonCnt?: number;
  /** the data of the plan and coverages offered */
  availablePlansAndCoverages: ProductAndCoveragesResponse[];
  /** the data of the price information for each plan */
  planPricingData: ProductPricingResponse[];
  /** event fired when a plan was selected */
  onSelectPlan: (plan: Product) => void;
  onMFELoadIndicator: (isLoading: boolean) => void;
  /** the property address for promo*/
  address?: Address;
  /** military discount is applied */
  isMilitaryDiscountApplied?: boolean;
  /** first responder discount is applied */
  isFirstResponderDiscountApplied?: boolean;
  /** promo code applied */
  voucherPromoCode?: string;
}

interface PlanItem {
  id: string;
  name: string;
  price: number;
  priceWithoutPromotion?: number;
  serviceFees: number;
  isActive: boolean;
  includedCoverageNames: string[];
  onClick: () => void;
  productPlanId?: string;
}

//temporary function to add required coverages for HBW210 Supreme and Standard plans. All this will be discarded when we switch to MFE
const addHBW210Coverages = (
  includedCoverage: { id: string; name: string; tags: string[]; starCoverageId: string }[],
  planName: string,
) => {
  const requiredCoverages = [] as {
    id: string;
    name: string;
    tags: string[];
    starCoverageId: string;
  }[];

  if (planName === HBW210Plan_Supreme) {
    requiredCoverages.push(
      { name: 'Kitchen Appliances', id: 'TEST001', tags: [], starCoverageId: '' },
      { name: 'Laundry Appliances', id: 'TEST002', tags: [], starCoverageId: '' },
    );
  } else if (planName === HBW210Plan_Standard) {
    requiredCoverages.push({
      name: 'Kitchen Appliances',
      id: 'TEST001',
      tags: [],
      starCoverageId: '',
    });
  }

  const existingIds = new Set(includedCoverage.map((cvg) => cvg.id));

  requiredCoverages.forEach((item) => {
    if (!existingIds.has(item.id)) {
      includedCoverage.push(item);
      existingIds.add(item.id);
    }
  });
};

const HBW210Plan_Supreme = 'Supreme';
const HBW210Plan_Standard = 'Standard';
const HBW210PlanNames = [HBW210Plan_Supreme, HBW210Plan_Standard];

const FormNewOrderPlanSelection: React.FC<FormNewOrderPlanSelectionProps> = ({
  selectedProductId,
  selectedProductPlanId,
  availablePlansAndCoverages,
  planPricingData,
  onSelectPlan,
  onMFELoadIndicator,
  isMilitaryDiscountApplied = false,
  isFirstResponderDiscountApplied = false,
  voucherPromoCode = '',
  ...props
}) => {
  const zestyUrl = isProdEnv()
    ? 'https://productcoverage.zestyapi.frontdoorhome.com'
    : 'https://productcoverage-dev.zesty.dev';

  const plans: PlanItem[] = useMemo(() => {
    if (!availablePlansAndCoverages?.length || !planPricingData?.length) {
      return [];
    }

    return availablePlansAndCoverages
      .map<PlanItem>(({ product, coverages }) => {
        // Check if active by starPVID (exact match) or by productPlanId (only if starPVID doesn't match)
        const isActive =
          selectedProductId === product.starPVID ||
          (selectedProductPlanId &&
            selectedProductPlanId === product.starProductPlanID &&
            !availablePlansAndCoverages.some((p) => p.product.starPVID === selectedProductId));

        const pricingData = planPricingData.find((data) => data.product.id === product.starPVID);

        // if brand == HBW210 and plan Supreme/Standard, ensure required coverages exist and avoid duplicates
        if (getBrand() === Brand.HBW210 && HBW210PlanNames.includes(product.name)) {
          addHBW210Coverages(coverages.included, product.name);
        }

        return {
          id: product.id,
          name: product.name,
          serviceFees: product.serviceFees,
          isActive,
          includedCoverageNames: coverages.included
            .map((cvg) => cvg.name)
            .sort((a, b) => a.localeCompare(b)),
          price: pricingData?.product.price,
          priceWithoutPromotion: pricingData?.product.priceWithoutPromotion,
          onClick: () => onSelectPlan(product),
          productPlanId: product.starProductPlanID,
        };
      })
      .sort((a, b) => a.price - b.price);
  }, [
    availablePlansAndCoverages,
    planPricingData,
    selectedProductId,
    selectedProductPlanId,
    onSelectPlan,
  ]);

  const plansCardsData = useMemo(() => {
    if (plans.length > 0) {
      return plans.map((plan) => {
        const priceWithPromo = plan.price;
        const priceWithoutPromo = plan.priceWithoutPromotion;

        const hasPromo = !!voucherPromoCode;
        const hasDiscount = isMilitaryDiscountApplied || isFirstResponderDiscountApplied;
        const hasModifiedPrice = (hasPromo || hasDiscount) && (priceWithoutPromo != priceWithPromo);

        const regularPrice = priceWithoutPromo || priceWithPromo || 0;
        const promotionalPrice = priceWithPromo || 0;

        const regularPriceDisplay = regularPrice.toString();
        const promotionalPriceDisplay = hasModifiedPrice ? promotionalPrice.toString() : '';

        return {
          productPlanID: plan.productPlanId,
          promoted: hasModifiedPrice,
          regularPrice: regularPriceDisplay,
          promotionalPrice: promotionalPriceDisplay,
          termSymbol: '',
        };
      });
    }
    return [];
  }, [plans, voucherPromoCode, isMilitaryDiscountApplied, isFirstResponderDiscountApplied]);

  const activePlanCardData = useMemo(() => {
    const activePlan = plans.find((plan) => plan.isActive);
    if (!activePlan) return null;
    return plansCardsData.find((card) => card.productPlanID === activePlan.productPlanId) || null;
  }, [plans, plansCardsData]);

  const { addErrorToQueue } = useGlobalAlert();

  const handleSelect = useCallback(
    (id: string) => {
      console.log(`${id} is selected`);
      const planContent = availablePlansAndCoverages.find(
        ({ product }) => product.starProductPlanID === id,
      )?.product;
      if (planContent) {
        onSelectPlan(planContent);
      }
    },
    [availablePlansAndCoverages, onSelectPlan],
  );

  const onErrorMfe = useCallback((error) => {
    addErrorToQueue({
      id: 'product-plans-onSelectPlan-error',
      message: 'Failed on select Plan',
    });
    console.error('ProductComparisonCardsMicroFrontend error', error);
  }, []);

  return (
    <div className="w-full pt-2 overflow-x-auto" id="plan-selection-container">
      <FormFieldset>
        {getCurrentThemeConfig().displayNewOrdersPlanMFE ? (
          <>
            {props.isLoading && (
              <div
                className={`justify-center md:flex md:-mx-2 lg:-mx-4 sm-max:space-y-2 sm-max:mx-2 ${getCurrentThemeConfig().newOrderPlansContainerLoader ? 'p-3' : ''}`}
              >
                {props.isLoading && (
                  <>
                    {Array(props.loadingSkeletonCnt || 3)
                      .fill(null)
                      .map((_, idx) => (
                        <div key={idx} className="md:w-1/3 sm-max:-mx-4 md:px-2 lg:px-2 xl:px-4">
                          <CardPlan isLoading={true} />
                        </div>
                      ))}
                  </>
                )}
              </div>
            )}
            {!props.isLoading && (
              <div className="w-full pt-4 product-comparison-cards-micro-frontend">
                <ProductComparisonCardsMicroFrontend
                  tenant={getCurrentThemeConfig().plansPricesMFETenant as Tenant}
                  theme={getCurrentThemeConfig().plansPricesMFETheme as Theme}
                  cmsBaseURL={zestyUrl}
                  language="en"
                  planTitleAlignment="left"
                  productDetailsList={plansCardsData}
                  showIncludedCoverageLevels
                  showExcludedPlanHighlights={false}
                  showPlanIcons={false}
                  onProductPlanSelect={handleSelect}
                  onLoadIndicator={onMFELoadIndicator}
                  initialSelectedPlan={activePlanCardData?.productPlanID}
                  selectPlanButtonColor={BDSMainColor.Primary}
                  onError={onErrorMfe}
                />
              </div>
            )}
          </>
        ) : (
          <div className="w-full pt-4">
            <div
              className={`justify-center md:flex md:-mx-2 lg:-mx-4 sm-max:space-y-2 sm-max:mx-2 ${getCurrentThemeConfig().newOrderPlansContainerLoader ? 'p-3' : ''}`}
            >
              {props.isLoading && (
                <>
                  {Array(props.loadingSkeletonCnt || 3)
                    .fill(null)
                    .map((_, idx) => (
                      <div key={idx} className="md:w-1/3 sm-max:-mx-4 md:px-2 lg:px-2 xl:px-4">
                        <CardPlan isLoading={true} />
                      </div>
                    ))}
                </>
              )}
              {!props.isLoading &&
                plans.map((plan) => (
                  <div key={plan.id} className="md:w-1/3 sm-max:-mx-4 md:px-2 lg:px-2 xl:px-4">
                    <CardPlan
                      id={`${sanitizeId(plan.name)}--card`}
                      isDrawerView={props.isSmall}
                      heading={
                        <>
                          {plan.name}
                          <sup>SM</sup>
                        </>
                      }
                      price={plan.price}
                      priceWithoutPromotion={plan.priceWithoutPromotion}
                      serviceFee={plan.serviceFees}
                      onClick={plan.onClick}
                      isActive={plan.isActive}
                      details={plan.includedCoverageNames}
                      promoData={voucherPromoCode ? { promoCode: voucherPromoCode } : null}
                      isMilitaryDiscountApplied={isMilitaryDiscountApplied}
                      isFirstResponderDiscountApplied={isFirstResponderDiscountApplied}
                    />
                  </div>
                ))}
            </div>
            {props.children}
          </div>
        )}
      </FormFieldset>
    </div>
  );
};

export default FormNewOrderPlanSelection;
